import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, LogOut, Users, Briefcase, FileText, Settings, DollarSign,
  Menu, X, TrendingUp, Eye, Edit, Trash2, Plus, Check, XCircle, Wrench,
  BadgeCheck, Shield, MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [homeowners, setHomeowners] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [jobRequests, setJobRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Dialogs
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', image_url: '' });
  
  // App Config (Verified Pro Perk settings)
  const [appConfig, setAppConfig] = useState({
    verified_pro_perk_enabled: false,
    verified_pro_perk_min_tier: 'Pro'
  });
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, revenueRes, homeownersRes, contractorsRes, jobsRes, catsRes, configRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/revenue`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/homeowners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/contractors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/job-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/app-config`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setRevenue(revenueRes.data);
      setHomeowners(homeownersRes.data);
      setContractors(contractorsRes.data);
      setJobRequests(jobsRes.data);
      setCategories(catsRes.data);
      if (configRes.data) {
        setAppConfig({
          verified_pro_perk_enabled: configRes.data.verified_pro_perk_enabled || false,
          verified_pro_perk_min_tier: configRes.data.verified_pro_perk_min_tier || 'Pro'
        });
      }
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleUpdateJobStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/job-requests/${requestId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.category_id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Category created");
      }
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', description: '', image_url: '' });
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm("Are you sure you want to delete this category?")) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || ''
    });
    setShowCategoryDialog(true);
  };

  const handleSaveAppConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/admin/app-config`, appConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("App configuration saved");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-manrope font-bold text-[#1F2933]">Contractor Hub LLC</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-white">Contractor Hub LLC</span>
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">{user?.name}</p>
                <p className="text-white/60 text-xs">Administrator</p>
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            {[
              { id: 'overview', icon: TrendingUp, label: 'Overview' },
              { id: 'homeowners', icon: Users, label: 'Homeowners' },
              { id: 'contractors', icon: Briefcase, label: 'Contractors', link: '/admin/contractors' },
              { id: 'leads', icon: Eye, label: 'Leads', link: '/admin/leads' },
              { id: 'jobs', icon: FileText, label: 'Job Requests' },
              { id: 'categories', icon: Settings, label: 'Categories' },
              { id: 'catalog', icon: Wrench, label: 'Pricing Catalog', link: '/admin/catalog' },
              { id: 'waitlist', icon: MapPin, label: 'Expansion Waitlist', link: '/admin/waitlist' },
              { id: 'revenue', icon: DollarSign, label: 'Revenue' },
              { id: 'settings', icon: Shield, label: 'App Settings' }
            ].map(item => (
              item.link ? (
                <Link key={item.id} to={item.link} className="sidebar-link w-full text-left">
                  <item.icon className="w-5 h-5" /> {item.label}
                </Link>
              ) : (
                <button 
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                  className={`sidebar-link w-full text-left ${activeTab === item.id ? 'active' : ''}`}
                >
                  <item.icon className="w-5 h-5" /> {item.label}
                </button>
              )
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button 
            onClick={handleLogout}
            className="sidebar-link w-full text-left text-red-300 hover:bg-red-500/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[260px] p-6 lg:p-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Admin Dashboard
                  </h1>
                  <p className="text-[#1F2933]/60">Overview of your marketplace</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: "Total Homeowners", value: stats?.total_homeowners || 0, color: "bg-blue-500", icon: Users },
                    { label: "Total Contractors", value: stats?.total_contractors || 0, color: "bg-purple-500", icon: Briefcase },
                    { label: "Active Subscriptions", value: stats?.active_contractors || 0, color: "bg-[#7ED957]", icon: Check },
                    { label: "Total Revenue", value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, color: "bg-[#0FA3B1]", icon: DollarSign }
                  ].map((stat) => (
                    <div key={stat.label} className="stat-card">
                      <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mb-3`}>
                        <stat.icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-[#1F2933]">{stat.value}</p>
                      <p className="text-sm text-[#1F2933]/60">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: "Total Jobs", value: stats?.total_jobs || 0 },
                    { label: "Pending Jobs", value: stats?.pending_jobs || 0 },
                    { label: "Completed Jobs", value: stats?.completed_jobs || 0 },
                    { label: "Categories", value: stats?.total_categories || 0 }
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
                      <p className="text-xl font-semibold text-[#1F2933]">{stat.value}</p>
                      <p className="text-sm text-[#1F2933]/60">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick Management Links */}
                <div className="mb-8">
                  <h2 className="font-manrope font-semibold text-lg text-[#1F2933] mb-4">Business Configuration</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Link 
                      to="/admin/subscription-plans"
                      className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow group"
                      data-testid="link-subscription-plans"
                    >
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-3 group-hover:bg-purple-200 transition-colors">
                        <Settings className="w-5 h-5 text-purple-600" />
                      </div>
                      <h3 className="font-semibold text-[#1F2933]">Subscription Plans</h3>
                      <p className="text-sm text-[#1F2933]/60">Manage Starter, Pro, Elite tiers</p>
                    </Link>
                    <Link 
                      to="/admin/credit-packs"
                      className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow group"
                      data-testid="link-credit-packs"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#0FA3B1]/10 flex items-center justify-center mb-3 group-hover:bg-[#0FA3B1]/20 transition-colors">
                        <DollarSign className="w-5 h-5 text-[#0FA3B1]" />
                      </div>
                      <h3 className="font-semibold text-[#1F2933]">Credit Packs</h3>
                      <p className="text-sm text-[#1F2933]/60">Manage credit top-up packs</p>
                    </Link>
                    <Link 
                      to="/admin/lead-pricing"
                      className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow group"
                      data-testid="link-lead-pricing"
                    >
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <h3 className="font-semibold text-[#1F2933]">Lead Pricing Rules</h3>
                      <p className="text-sm text-[#1F2933]/60">Set credit costs & insurance gating</p>
                    </Link>
                    <Link 
                      to="/admin/subscriptions"
                      className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow group"
                      data-testid="link-subscription-management"
                    >
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3 group-hover:bg-emerald-200 transition-colors">
                        <Users className="w-5 h-5 text-emerald-600" />
                      </div>
                      <h3 className="font-semibold text-[#1F2933]">Contractor Subscriptions</h3>
                      <p className="text-sm text-[#1F2933]/60">Manage demo, trial & paid access</p>
                    </Link>
                  </div>
                </div>

                {/* Recent Job Requests */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-manrope font-semibold text-lg text-[#1F2933]">Recent Job Requests</h2>
                    <Button variant="ghost" onClick={() => setActiveTab("jobs")}>View All</Button>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Homeowner</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobRequests.slice(0, 5).map((job) => (
                        <TableRow key={job.request_id}>
                          <TableCell className="font-medium">{job.title}</TableCell>
                          <TableCell>{job.category_name}</TableCell>
                          <TableCell>{job.homeowner_name}</TableCell>
                          <TableCell>
                            <Badge className={
                              job.status === 'completed' ? 'bg-[#7ED957]/20 text-green-800' :
                              job.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-[#0FA3B1]/15 text-[#0FA3B1]'
                            }>
                              {job.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[#1F2933]/60">
                            {new Date(job.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Homeowners Tab */}
            {activeTab === "homeowners" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Homeowners
                  </h1>
                  <p className="text-[#1F2933]/60">Manage homeowner accounts</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {homeowners.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.phone || '-'}</TableCell>
                          <TableCell className="text-[#1F2933]/60">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {homeowners.length === 0 && (
                    <div className="text-center py-12 text-[#1F2933]/60">No homeowners yet</div>
                  )}
                </div>
              </div>
            )}

            {/* Contractors Tab */}
            {activeTab === "contractors" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Contractors
                  </h1>
                  <p className="text-[#1F2933]/60">Manage contractor accounts</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Leads Clicked</TableHead>
                        <TableHead>Revenue Generated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contractors.map((contractor) => (
                        <TableRow key={contractor.user_id}>
                          <TableCell className="font-medium">
                            {contractor.profile?.business_name || contractor.name}
                          </TableCell>
                          <TableCell>{contractor.email}</TableCell>
                          <TableCell>
                            <Badge className={
                              contractor.profile?.subscription_status === 'active' 
                                ? 'bg-[#7ED957]/20 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }>
                              {contractor.profile?.subscription_status || 'inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>{contractor.profile?.total_leads_clicked || 0}</TableCell>
                          <TableCell>${contractor.profile?.total_revenue_generated?.toFixed(2) || '0.00'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {contractors.length === 0 && (
                    <div className="text-center py-12 text-[#1F2933]/60">No contractors yet</div>
                  )}
                </div>
              </div>
            )}

            {/* Job Requests Tab */}
            {activeTab === "jobs" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Job Requests
                  </h1>
                  <p className="text-[#1F2933]/60">Manage all job requests</p>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Homeowner</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobRequests.map((job) => (
                        <TableRow key={job.request_id}>
                          <TableCell className="font-medium">{job.title}</TableCell>
                          <TableCell>{job.category_name}</TableCell>
                          <TableCell>{job.homeowner_name}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{job.address}</TableCell>
                          <TableCell>
                            <Select
                              value={job.status}
                              onValueChange={(value) => handleUpdateJobStatus(job.request_id, value)}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="matched">Matched</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {jobRequests.length === 0 && (
                    <div className="text-center py-12 text-[#1F2933]/60">No job requests yet</div>
                  )}
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === "categories" && (
              <div>
                <div className="mb-8 flex items-center justify-between">
                  <div>
                    <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                      Categories
                    </h1>
                    <p className="text-[#1F2933]/60">Manage service categories</p>
                  </div>
                  <Button 
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryForm({ name: '', description: '', image_url: '' });
                      setShowCategoryDialog(true);
                    }}
                    className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full"
                    data-testid="add-category-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" /> Add Category
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.map((category) => (
                    <div 
                      key={category.category_id}
                      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                    >
                      <div className="h-32 overflow-hidden">
                        <img 
                          src={category.image_url} 
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-[#1F2933]">{category.name}</h3>
                        <p className="text-sm text-[#1F2933]/60 line-clamp-2">{category.description}</p>
                        <div className="flex gap-2 mt-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openEditCategory(category)}
                          >
                            <Edit className="w-4 h-4 mr-1" /> Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteCategory(category.category_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revenue Tab */}
            {activeTab === "revenue" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    Revenue
                  </h1>
                  <p className="text-[#1F2933]/60">Track your marketplace revenue</p>
                </div>

                {/* Revenue Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="stat-card">
                    <div className="w-12 h-12 rounded-xl bg-[#0FA3B1] flex items-center justify-center mb-3">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-3xl font-bold text-[#1F2933]">${revenue?.total_revenue?.toFixed(2) || '0.00'}</p>
                    <p className="text-sm text-[#1F2933]/60">Total Revenue</p>
                  </div>
                  <div className="stat-card">
                    <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center mb-3">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-3xl font-bold text-[#1F2933]">${revenue?.subscription_revenue?.toFixed(2) || '0.00'}</p>
                    <p className="text-sm text-[#1F2933]/60">Subscription Revenue</p>
                  </div>
                  <div className="stat-card">
                    <div className="w-12 h-12 rounded-xl bg-[#7ED957] flex items-center justify-center mb-3">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-3xl font-bold text-[#1F2933]">${revenue?.lead_click_revenue?.toFixed(2) || '0.00'}</p>
                    <p className="text-sm text-[#1F2933]/60">Lead Click Revenue</p>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <h2 className="font-manrope font-semibold text-lg text-[#1F2933]">Monthly Breakdown</h2>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Subscriptions</TableHead>
                        <TableHead>Lead Clicks</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(revenue?.monthly_breakdown || {}).map(([month, data]) => (
                        <TableRow key={month}>
                          <TableCell className="font-medium">{month}</TableCell>
                          <TableCell>${data.subscriptions?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>${data.lead_clicks?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell className="font-semibold">${data.total?.toFixed(2) || '0.00'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {Object.keys(revenue?.monthly_breakdown || {}).length === 0 && (
                    <div className="text-center py-12 text-[#1F2933]/60">No revenue data yet</div>
                  )}
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === "settings" && (
              <div>
                <div className="mb-8">
                  <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
                    App Settings
                  </h1>
                  <p className="text-[#1F2933]/60">Configure marketplace features and rules</p>
                </div>

                {/* Verified Pro Perk Settings */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm max-w-2xl" data-testid="verified-pro-settings">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#7ED957] flex items-center justify-center">
                      <BadgeCheck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="font-manrope font-bold text-lg text-[#1F2933]">Verified Pro Badge Settings</h2>
                      <p className="text-sm text-[#1F2933]/60">Control who can earn the Verified Pro badge</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <Label className="text-[#1F2933] font-medium">Enable Verified Pro as Pro/Elite Perk</Label>
                        <p className="text-sm text-[#1F2933]/60 mt-1">
                          When enabled, only Pro and Elite tier subscribers can earn the Verified Pro badge
                        </p>
                      </div>
                      <button
                        onClick={() => setAppConfig(prev => ({ 
                          ...prev, 
                          verified_pro_perk_enabled: !prev.verified_pro_perk_enabled 
                        }))}
                        className={`relative w-14 h-7 rounded-full transition-colors ${
                          appConfig.verified_pro_perk_enabled ? 'bg-[#7ED957]' : 'bg-gray-300'
                        }`}
                        data-testid="verified-pro-toggle"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${
                          appConfig.verified_pro_perk_enabled ? 'translate-x-7' : ''
                        }`} />
                      </button>
                    </div>

                    {/* Minimum Tier Dropdown */}
                    <div className={`p-4 bg-gray-50 rounded-xl ${!appConfig.verified_pro_perk_enabled && 'opacity-50'}`}>
                      <Label className="text-[#1F2933] font-medium">Minimum Tier Required</Label>
                      <p className="text-sm text-[#1F2933]/60 mb-3">
                        Select the minimum subscription tier required to earn Verified Pro
                      </p>
                      <Select
                        value={appConfig.verified_pro_perk_min_tier}
                        onValueChange={(value) => setAppConfig(prev => ({ ...prev, verified_pro_perk_min_tier: value }))}
                        disabled={!appConfig.verified_pro_perk_enabled}
                      >
                        <SelectTrigger className="w-full max-w-xs" data-testid="min-tier-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pro">Pro</SelectItem>
                          <SelectItem value="Elite">Elite</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Current Status */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <p className="text-blue-800 text-sm">
                        <strong>Current Status:</strong>{' '}
                        {appConfig.verified_pro_perk_enabled 
                          ? `Verified Pro is restricted to ${appConfig.verified_pro_perk_min_tier}+ tier subscribers`
                          : 'Verified Pro is available to all tiers (MVP mode)'}
                      </p>
                    </div>

                    {/* Save Button */}
                    <Button
                      onClick={handleSaveAppConfig}
                      disabled={savingConfig}
                      className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
                      data-testid="save-config-btn"
                    >
                      {savingConfig ? (
                        <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white"></div>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" /> Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update the category details' : 'Create a new service category'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cat-name">Name</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="mt-2"
                placeholder="e.g., Pool Cleaning"
              />
            </div>
            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Input
                id="cat-desc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                className="mt-2"
                placeholder="Professional pool cleaning services..."
              />
            </div>
            <div>
              <Label htmlFor="cat-image">Image URL</Label>
              <Input
                id="cat-image"
                value={categoryForm.image_url}
                onChange={(e) => setCategoryForm({ ...categoryForm, image_url: e.target.value })}
                className="mt-2"
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white">
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
